import { Dispatch } from 'react-redux';
import axios, { AxiosError, AxiosResponse } from "axios";
import * as _ from "lodash";

import { AuthActionCreators, IApplicationState } from "../";
import { IMiracleMatch, IFavorableBloodTypeCompatiblePair, IManualTargetedChainEnd, ITargetedChainEnd, IUnmatchedPair, IDonorsPerMonthByAbo, IBaseResponse, ITargetItem } from "@nkr/models/index";

// action types

export const ActionTypes = {
    SET_MIRACLE_MATCHES: "SET_MIRACLE_MATCHES",
    SET_FAVORABLE_BLOODTYPE_COMPATIBLE_PAIRS: "SET_FAVORABLE_BLOODTYPE_COMPATIBLE_PAIRS",
    SET_MANUAL_TARGETED_CHAIN_ENDS: "SET_MANUAL_TARGETED_CHAIN_ENDS",
    SET_TARGETED_CHAIN_ENDS: "SET_TARGETED_CHAIN_END",
    SET_UNMATCHED_PAIRS: "SET_UNMATCHED_PAIRS",
    SET_DONORS_PER_MONTH_BY_ABO: "SET_DONORS_PER_MONTH_BY_ABO"
}

// state interface

export interface ITargetState {
    miracleMatches: IMiracleMatch[],
    favorableBloodTypeCompatiblePairs: IFavorableBloodTypeCompatiblePair[],
    manualTargetedChainEnds: IManualTargetedChainEnd[],
    targetedChainEnds: ITargetedChainEnd[],
    unmatchedPairs: IUnmatchedPair[],
    donorsPerMonthByAbo: IDonorsPerMonthByAbo
}

// action interfaces

export interface IGetAllResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            miracleMatches: ITargetItem[],
            targetedChainEnds: ITargetItem[],
            unmatchedPairs: ITargetItem[]
        }
    }
}

export interface IGetAllNewResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            hardToMatchList: ITargetItem[],
            voucherList: ITargetItem[],
            pairedPatientList: ITargetItem[]
        }
    }
}

export interface IDonorsPerMonthByAboResponse {
    data: {
        success: boolean,
        message: string,
        results: IDonorsPerMonthByAbo
    }
}

export const ActionCreators = {
    getAll: () => async (dispatch: Dispatch<IApplicationState>): Promise<IGetAllResponse> => {
        let options = {
            url: __API__ + '/target/all'
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    getAllNew: () => async (dispatch: Dispatch<IApplicationState>): Promise<IGetAllNewResponse> => {
        let options = {
            url: __API__ + '/target/all-new'
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    getDonorsPerMonthByAbo: () => async (dispatch: Dispatch<IApplicationState>): Promise<IDonorsPerMonthByAboResponse> => {
        let options = {
            url: __API__ + '/target/donors-per-month-by-abo'
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    setDonorsPerMonthByAbo: (donorsPerMonthByAbo: IDonorsPerMonthByAbo) => {
        return { type: ActionTypes.SET_DONORS_PER_MONTH_BY_ABO, donorsPerMonthByAbo };
    },

    sendRexmEmail: (donorId: number, recipientId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: __API__ + `/best-match/update-rexm/${donorId}/${recipientId}`
        }

        return dispatch(AuthActionCreators.apiGet(options))
            .then((response: IBaseResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },

    updateClc: () => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: __API__ + '/target/update-clc'
        }

        return dispatch(AuthActionCreators.apiGet(options))
            .then((response: IBaseResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    }
}

// reducer interfaces

interface ISetMiracleMatches {
    type: string,
    miracleMatches: IMiracleMatch[]
}

interface ISetFavorableBloodTypeCompatiblePair {
    type: string,
    favorableBloodTypeCompatiblePairs: IFavorableBloodTypeCompatiblePair[]
}

interface ISetManualTargetedChainEnds {
    type: string,
    manualTargetedChainEnds: IManualTargetedChainEnd[]
}

interface ISetTargetedChainEnds {
    type: string,
    targetedChainEnds: ITargetedChainEnd[]
}

interface ISetUnmatchedPairs {
    type: string,
    unmatchedPairs: IUnmatchedPair[]
}

interface ISetDonorsPerMonthByAbo {
    type: string,
    donorsPerMonthByAbo: IDonorsPerMonthByAbo
}

// reducer functions

const setMiraclesMatches = (state: ITargetState, action: ISetMiracleMatches) => {
    state.miracleMatches = action.miracleMatches;
    return state;
}

const setFavorableBloodTypeCompatiblePairs = (state: ITargetState, action: ISetFavorableBloodTypeCompatiblePair) => {
    state.favorableBloodTypeCompatiblePairs = action.favorableBloodTypeCompatiblePairs;
    return state;
}

const setManualTargetedChainEnds = (state: ITargetState, action: ISetManualTargetedChainEnds) => {
    state.manualTargetedChainEnds = action.manualTargetedChainEnds;
    return state;
}

const setTargetedChainEnds = (state: ITargetState, action: ISetTargetedChainEnds) => {
    state.targetedChainEnds = action.targetedChainEnds;
    return state;
}

const setUnmatchedPairs = (state: ITargetState, action: ISetUnmatchedPairs) => {
    state.unmatchedPairs = action.unmatchedPairs;
    return state;
}

const setDonorsPerMonthByAbo = (state: ITargetState, action: ISetDonorsPerMonthByAbo) => {
    state.donorsPerMonthByAbo = action.donorsPerMonthByAbo;
    return state;
}

// reducer creation

var initialState: ITargetState = {
    miracleMatches: [],
    favorableBloodTypeCompatiblePairs: [],
    manualTargetedChainEnds: [],
    targetedChainEnds: [],
    unmatchedPairs: [],
    donorsPerMonthByAbo: null
}

const createReducer = (initialState: ITargetState, handlers: any) => {
    return function reducer(state = initialState, action: any) {
        if (handlers.hasOwnProperty(action.type)) {
            let s = _.cloneDeep(state);
            return handlers[action.type](s, action);
        } else {
            return state;
        }
    }
}

const handlers = {
    [ActionTypes.SET_MIRACLE_MATCHES]: setMiraclesMatches,
    [ActionTypes.SET_FAVORABLE_BLOODTYPE_COMPATIBLE_PAIRS]: setFavorableBloodTypeCompatiblePairs,
    [ActionTypes.SET_MANUAL_TARGETED_CHAIN_ENDS]: setManualTargetedChainEnds,
    [ActionTypes.SET_TARGETED_CHAIN_ENDS]: setTargetedChainEnds,
    [ActionTypes.SET_UNMATCHED_PAIRS]: setUnmatchedPairs,
    [ActionTypes.SET_DONORS_PER_MONTH_BY_ABO]: setDonorsPerMonthByAbo
}

export const Reducer = createReducer(initialState, handlers);